import React, { useCallback, useMemo, useState } from 'react';
import { isEqual, sumBy } from 'lodash';

import { Box, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import Edit from '@mui/icons-material/Edit';

import { formatCurrencyFloat } from '../../../../../../shared/utils/numberFormatters';

import ReimbursementEditModal from '../edit-modal/ReimbursementEditModal';
import { CommercialReimbursement, DollarAmountReimbursement } from 'types/underwriting/income-approach';
import { Field } from 'mobx-react-form';
import NumericInput from 'components/shared/inputs/NumericInput';
import { DEFAULT_EXPENSES_TITLES } from 'shared/constants/expenses';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { renderCheckboxCell } from 'components/underwriting/utils/cell-renders';
import { observer } from 'mobx-react-lite';

type DollarAmountReimbursementTableProps = {
  reimbursementField: Field;
  onUpdate: (reimbursement: CommercialReimbursement) => void;
  onDelete: () => void;
};

const DollarAmountReimbursementTable: React.FC<DollarAmountReimbursementTableProps> = observer(
  ({ reimbursementField, onUpdate, onDelete }) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const unitReimbursementsField: Field = reimbursementField.$('unitReimbursements');
    const vcLossField: Field = reimbursementField.$('vcLoss');
    const grossVCLossField: Field = reimbursementField.$('grossVCLoss');
    const grossAnnualValueField: Field = reimbursementField.$('grossAnnualValue');
    const effectiveAnnualValueField: Field = reimbursementField.$('effectiveAnnualValue');

    const openEditModal = () => {
      setIsEditModalOpen(true);
    };
    const closeEditModal = () => {
      setIsEditModalOpen(false);
    };

    const updateReimbursement = (updatedReimbursement: CommercialReimbursement) => {
      onUpdate(updatedReimbursement);
      closeEditModal();
    };
    const deleteReimbursement = () => {
      onDelete();
    };

    const recalculateReimbursementValues = useCallback(() => {
      const reimbursement: DollarAmountReimbursement = reimbursementField.value as DollarAmountReimbursement;

      const newGrossAnnualValue = sumBy(
        reimbursement.unitReimbursements.filter((unitReimbursement) => unitReimbursement.enabled),
        (unitReimbursement) => unitReimbursement.annual || 0
      );
      grossAnnualValueField.set(newGrossAnnualValue);

      const newGrossVCLossValue = (newGrossAnnualValue || 0) * ((reimbursement.vcLoss || 0) / 100);
      grossVCLossField.set(newGrossVCLossValue);

      const newEffectiveAnnualValue = (newGrossAnnualValue || 0) - (newGrossVCLossValue || 0);
      effectiveAnnualValueField.set(newEffectiveAnnualValue);
    }, [reimbursementField.value, grossAnnualValueField, effectiveAnnualValueField, grossVCLossField]);

    const onRowUpdate = useCallback(
      ({ id, ...newRow }: any, oldRow: any) => {
        const reimbursement: DollarAmountReimbursement = reimbursementField.value as DollarAmountReimbursement;
        const monthlyAmountKnown = reimbursement.dollarAmountType === 'monthly';
        if (newRow.isTotalRow || isEqual(newRow, oldRow)) {
          return oldRow;
        }

        let updatedNewRow = newRow;

        let updatedUnitReimbursements = (unitReimbursementsField.value || []).map((unitReimbursement: any) => {
          if (unitReimbursement.unitId === newRow.unitId) {
            if (monthlyAmountKnown) {
              updatedNewRow.annual = (newRow.monthly || 0) * 12;
            }
            return updatedNewRow;
          }
          return unitReimbursement;
        });

        unitReimbursementsField.set(updatedUnitReimbursements);
        recalculateReimbursementValues();

        return updatedNewRow;
      },
      [recalculateReimbursementValues, unitReimbursementsField, reimbursementField.value]
    );

    const onRowEnabledChange = useCallback(
      (row: any, checked: boolean) => {
        const targetField = unitReimbursementsField
          .map((field: Field) => field)
          .find((field) => {
            return field.value.unitId === row.unitId;
          });

        if (targetField) {
          targetField.set({ ...targetField.value, enabled: checked });
          recalculateReimbursementValues();
        }
      },
      [unitReimbursementsField, recalculateReimbursementValues]
    );

    const gridColumns: GridColDef[] = useMemo(() => {
      const reimbursement: DollarAmountReimbursement = reimbursementField.value as DollarAmountReimbursement;
      const monthlyAmountKnown = reimbursement.dollarAmountType === 'monthly';
      const monthlyColumn: GridColDef = {
        field: 'monthly',
        headerName: 'Monthly Reimbursement',
        minWidth: 350,
        flex: 1,
        align: 'right',
        editable: true,
        type: 'number',
        valueFormatter: (value: any) => {
          if (value == null || value === '') return '';
          return formatCurrencyFloat(value);
        },
        renderCell: (params: any) => {
          if (params.row.isTotalRow) {
            return '';
          }
          if (params.value == null || params.value === '') return '';
          return formatCurrencyFloat(params.value);
        },
      };
      return [
        {
          field: 'enabled',
          headerName: '',
          width: 50,
          align: 'center',
          editable: true,
          type: 'boolean',
          renderCell: (params) => renderCheckboxCell(params, onRowEnabledChange),
        },
        {
          field: 'tenant',
          headerName: 'Tenant Name',
          minWidth: 350,
          flex: 1,
          align: 'left',
          editable: false,
          type: 'string',
          renderCell: (params: any) => {
            if (params.row.isTotalRow) {
              return 'Total:';
            }
            return params.value;
          },
        },
        ...(monthlyAmountKnown ? [monthlyColumn] : []),
        {
          field: 'annual',
          headerName: 'Annual Reimbursement',
          minWidth: 350,
          flex: 1,
          align: 'right',
          editable: !monthlyAmountKnown,
          type: 'number',
          valueFormatter: (value: any) => {
            if (value == null || value === '') return '';
            return formatCurrencyFloat(value);
          },
          renderCell: (params: any) => {
            if (params.value == null || params.value === '') return '';
            return formatCurrencyFloat(params.value);
          },
        },
      ];
    }, [onRowEnabledChange, reimbursementField.value]);

    const renderUnitReimbursementTable = () => {
      const reimbursement: DollarAmountReimbursement = reimbursementField.value as DollarAmountReimbursement;
      const monthlyAmountKnown = reimbursement.dollarAmountType === 'monthly';
      const unitReimbursementRows = reimbursement.unitReimbursements.map((unitReimbursement) => {
        return {
          ...unitReimbursement,
          id: unitReimbursement.unitId,
        };
      });

      const rowsWithIds = [
        ...unitReimbursementRows,
        {
          id: 'annual-total',
          tenant: 'Total:',
          annual: reimbursement.grossAnnualValue,
          isTotalRow: true,
        },
      ];

      return (
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={rowsWithIds}
            columns={gridColumns}
            processRowUpdate={onRowUpdate}
            onProcessRowUpdateError={(error) => {
              console.log({ error });
            }}
            hideFooter
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableColumnSorting
            disableColumnResize
            getRowHeight={() => 52}
            getRowId={(row) => {
              return row.unitId || 'annual-total';
            }}
            rowBufferPx={52 * 20}
            getCellClassName={(params) => {
              if (params.row.isTotalRow) {
                return 'disabled-cell';
              }

              if (params.field === 'tenant') {
                return 'disabled-cell';
              }

              if (params.field === 'annual' && monthlyAmountKnown) {
                return 'disabled-cell';
              }
              return '';
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #e0e0e0',
                borderRight: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                borderBottom: '2px solid #e0e0e0',
              },

              '& .MuiDataGrid-row[data-id="annual-total"]': {
                backgroundColor: 'rgb(245, 245, 245)',
                '& .MuiDataGrid-cell': {
                  backgroundColor: 'rgb(245, 245, 245)',
                  color: '#666',
                  fontWeight: 'bold',
                },
              },
              '& .disabled-cell': {
                backgroundColor: 'rgb(245, 245, 245)',
                color: '#666',
                fontWeight: 'normal',
              },
              '& .MuiDataGrid-cell input[type="number"]::-webkit-outer-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0,
              },
              '& .MuiDataGrid-cell input[type="number"]::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0,
              },
              '& .MuiDataGrid-cell input[type="number"]': {
                '-moz-appearance': 'textfield',
              },
            }}
            isCellEditable={(params) => {
              if (params.row.isTotalRow) {
                return false;
              }

              if (params.field === 'tenant') {
                return false;
              }

              if (params.field === 'annual' && monthlyAmountKnown) {
                return false;
              }

              return true;
            }}
          />
        </Box>
      );
    };

    const renderVCLossSection = () => {
      const reimbursement: DollarAmountReimbursement = reimbursementField.value as DollarAmountReimbursement;

      const expenseType = DEFAULT_EXPENSES_TITLES[reimbursement.expenseCategory] || reimbursement.expenseCategory;
      return (
        <Grid container justifyContent="space-between">
          <Grid item xs={4} padding="0px 16px">
            <NumericInput
              {...vcLossField.bind()}
              disabled
              label="VC Loss %"
              size="small"
              margin="normal"
              allowNegative={false}
              decimalScale={2}
              max={100}
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={4} padding="0px 16px">
            <NumericInput
              {...grossVCLossField.bind()}
              label="Gross V/C Loss"
              size="small"
              margin="normal"
              allowNegative={false}
              decimalScale={2}
              fullWidth
              disabled
              thousandSeparator
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={4} padding="0px 16px">
            <NumericInput
              {...effectiveAnnualValueField.bind()}
              label={`Effective ${expenseType} Reimbursement`}
              size="small"
              margin="normal"
              allowNegative={false}
              decimalScale={2}
              fullWidth
              disabled
              thousandSeparator
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      );
    };

    return (
      <React.Fragment>
        <Grid container direction="row" alignItems="center">
          <Typography>{(reimbursementField.value as DollarAmountReimbursement).name}</Typography>
          <IconButton onClick={openEditModal}>
            <Edit color="primary" />
          </IconButton>
          <IconButton onClick={deleteReimbursement}>
            <DeleteOutlined color="error" />
          </IconButton>
        </Grid>
        {isEditModalOpen && (
          <ReimbursementEditModal
            reimbursementField={reimbursementField}
            onSave={updateReimbursement}
            onCancel={closeEditModal}
            isCreateModal={false}
          />
        )}
        <Grid container width={'100%'}>
          <Grid item xs={12}>
            {renderUnitReimbursementTable()}
            {renderVCLossSection()}
          </Grid>
        </Grid>
      </React.Fragment>
    );
  }
);

export default DollarAmountReimbursementTable;
